import { memo } from "react";
import type { CampusFloodPrevention, CampusRoadFloodReport, FallbackTier } from "@chula/shared";
import { PanelHeader } from "./PanelHeader";
import { PanelState } from "./PanelState";
import { useFloodPrevention } from "../hooks/useFloodPrevention";

interface Props {
  apiBase: string;
  fallbackTier?: FallbackTier;
  ageMinutes?: number | null;
}

/**
 * Campus-area flood prevention — "specific info about flood prevention in
 * this particular area" view, per the product decision that campus
 * operators care about *near-campus* actionable items rather than the
 * national watch indicator alone.
 *
 * Renders:
 *   - The preparedness line (one operational sentence per language).
 *   - Nearby road reports: district · road · depth · status, newest/active first.
 *   - The 48 h rain forecast bucket (dry / moist / wet / saturated).
 *   - The Bangkok watch band (inherited from FloodDash's province 10).
 *
 * Compact enough to sit in the left rail below the existing FloodWatchPanel.
 */
export const FloodPreventionPanel = memo(function FloodPreventionPanel({
  apiBase,
  fallbackTier,
  ageMinutes,
}: Props) {
  const { data, error, isLoading } = useFloodPrevention(apiBase);
  const snap: CampusFloodPrevention | null = data?.features?.[0] ?? null;
  const tier: FallbackTier = (data?.meta?.fallbackTier as FallbackTier | undefined) ?? fallbackTier ?? "unavailable";

  if (isLoading && !snap) {
    // Tier is unknown until the first response lands — never OFFLINE while loading.
    return (
      <section className="left-section">
        <PanelHeader title="FLOOD PREVENTION" ageMinutes={ageMinutes} fallbackTier={null} source="flooddash-dds" />
        <PanelState kind="loading" what="campus-area prevention snapshot" />
      </section>
    );
  }

  if (error || !snap) {
    return (
      <section className="left-section">
        <PanelHeader
          title="FLOOD PREVENTION"
          ageMinutes={ageMinutes}
          fallbackTier="unavailable"
          source="flooddash-dds"
        />
        <PanelState kind="error" what="campus-area prevention snapshot" source="FloodDash" />
      </section>
    );
  }

  const activeReports = snap.nearbyRoadReports.filter((r) => !isClearedStatus(r.status));
  const recentCleared = snap.nearbyRoadReports.filter((r) => isClearedStatus(r.status)).slice(0, 3);

  return (
    <section className="left-section">
      <PanelHeader
        title="FLOOD PREVENTION"
        ageMinutes={ageMinutes}
        fallbackTier={tier}
        source="flooddash-dds+thaiwater+openmeteo"
        actions={
          <span
            className="mono caption"
            style={{ color: watchBandColor(snap.watchBand) }}
            title={`National Bangkok watch band: ${snap.watchBand}`}
          >
            {snap.watchBand.toUpperCase()}
          </span>
        }
      />

      {/* The one operational sentence — what the operator should do *now* */}
      <div className="flood-prep-preparedness">
        <div className="flood-prep-preparedness-line mono">{snap.preparednessLineEn}</div>
        <div className="flood-prep-preparedness-th caption">{snap.preparednessLineTh}</div>
      </div>

      {/* Nearby roads — the actionable rows */}
      <div className="flood-prep-nearby">
        <header className="flood-prep-nearby-head">
          <span className="caption mono" style={{ color: "var(--text-3)", letterSpacing: "0.08em" }}>
            NEARBY ROADS · 7 D
          </span>
          <span className="caption mono" style={{ color: "var(--text-3)" }}>
            {snap.activeNearbyRoads} ACTIVE / {snap.totalNearbyRoads} TOTAL
          </span>
        </header>

        {snap.nearbyRoadReports.length === 0 ? (
          <div className="caption mono flood-prep-empty">No flood reports within 7 d for districts near campus.</div>
        ) : (
          <ul className="flood-prep-list">
            {activeReports.slice(0, 6).map((r) => (
              <RoadRow key={r.id} r={r} active />
            ))}
            {recentCleared.length > 0 && activeReports.length > 0 && (
              <li className="flood-prep-divider caption mono" aria-hidden>
                — RECENTLY CLEARED —
              </li>
            )}
            {recentCleared.map((r) => (
              <RoadRow key={r.id} r={r} active={false} />
            ))}
          </ul>
        )}
      </div>

      {/* Forecast summary — the next 48 h rain bucket */}
      <div className="flood-prep-forecast">
        <span className="caption mono" style={{ color: "var(--text-3)", letterSpacing: "0.08em" }}>
          NEXT 48 H RAIN
        </span>
        <span
          className="mono flood-prep-forecast-val"
          style={{ color: rainBandColor(snap.rain48hBand, snap.rain48hMm) }}
          title={
            snap.rain48hMm != null
              ? `${snap.rain48hMm.toFixed(1)} mm forecast at campus centroid`
              : "Open-Meteo forecast unavailable"
          }
        >
          {snap.rain48hMm != null ? `${snap.rain48hMm.toFixed(0)} mm` : "—"}
          <span className="caption mono flood-prep-forecast-band">
            {snap.rain48hBand ? snap.rain48hBand.toUpperCase() : "NO DATA"}
          </span>
        </span>
      </div>
    </section>
  );
});

// ─── helpers ────────────────────────────────────────────────────────────────

function isClearedStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return s.includes("cleared") || s.includes("resolved") || s.includes("closed");
}

function depthClass(depthCm: number | null): string {
  if (depthCm == null) return "flood-prep-depth--none";
  if (depthCm >= 30) return "flood-prep-depth--high";
  if (depthCm >= 10) return "flood-prep-depth--med";
  return "flood-prep-depth--low";
}

function watchBandColor(b: CampusFloodPrevention["watchBand"]): string {
  switch (b) {
    case "high": return "var(--bad)";
    case "elevated": return "var(--neg)";
    case "watch": return "var(--warn)";
    case "normal": return "var(--good)";
    default: return "var(--text-3)";
  }
}

function rainBandColor(b: CampusFloodPrevention["rain48hBand"], _mm: number | null): string {
  if (b === "saturated") return "var(--bad)";
  if (b === "wet") return "var(--neg)";
  if (b === "moist") return "var(--warn)";
  if (b === "dry") return "var(--text-2)";
  return "var(--text-3)";
}

function RoadRow({ r, active }: { r: CampusRoadFloodReport; active: boolean }) {
  return (
    <li className={`flood-prep-row ${active ? "is-active" : "is-cleared"}`}>
      <span className="flood-prep-row-district caption mono" title={r.district}>
        {r.district.length > 14 ? `${r.district.slice(0, 13)}…` : r.district}
      </span>
      <span className="flood-prep-row-body">
        <span className="flood-prep-row-road">{r.road}</span>
        <span className="caption mono flood-prep-row-meta">
          {r.date}
          {r.lanes ? ` · ${r.lanes}` : ""}
          {r.location ? ` · ${r.location.length > 30 ? `${r.location.slice(0, 29)}…` : r.location}` : ""}
        </span>
      </span>
      <span className={`flood-prep-row-depth mono ${depthClass(r.depthCm)}`} title={r.depthCm != null ? `${r.depthCm} cm deep` : "depth unknown"}>
        {r.depthCm != null ? `${r.depthCm}cm` : "—"}
      </span>
      <span
        className={`flood-prep-row-status caption mono ${active ? "is-active" : "is-cleared"}`}
        title={r.status ?? ""}
      >
        {active ? "ACTIVE" : "CLEARED"}
      </span>
    </li>
  );
}
